import type { BugReport } from '../../types/bugReport';
import type { PrecedenceViolation } from '../audit/providerPrecedenceVerifier';
import type { IntentSedReconciliationResult } from '../audit/intentSedReconciler';
import { authedFetch } from '../authedFetch';
import { loadAppStore } from '../appStore/appStorePort';

declare const __BUILD_HASH__: string;

export type AuditTelemetryEventType =
  | 'report-generated'
  | 'reliability-marker-corrected'
  | 'audit-run'
  | 'audit-run-completed'
  | 'bug-submitted'
  | 'reconciliation-block'
  | 'precedence-violation'
  | 'citation-regeneration'
  | 'beta-banner-click'
  | 'section-drift-detected'
  | 'coverage-gate-block'
  | 'coverage-gate-override'
  | 'distribution-outlier'
  | 'caveat-honesty-violation'
  | 'provider-relevance-violation'
  | 'tour-started'
  | 'tour-step-viewed'
  | 'tour-completed'
  | 'tour-skipped'
  | 'review-loop-best-of-selected'
  | 'review-loop-iteration-selected'
  | 'site-state-required-error'
  | 'conflict-detect-stateless'
  | 'sed-rebuild-success'
  | 'sed-rebuild-caution'
  | 'sed-rebuild-blocked'
  | 'sed-rebuild-rolled-back'
  | 'sed-rebuild-overridden'
  | 'sed-bootstrap-started'
  | 'sed-bootstrap-completed'
  | 'sed-bootstrap-failed'
  | 'sed-bootstrap-skipped-fresh'
  | 'sed-uploads-topup'
  | 'sed-bootstrap-dedup-hit'
  | 'sed-bootstrap-force-refresh'
  | 'sed-bootstrap-lock-acquired'
  | 'sed-bootstrap-lock-released'
  | 'sed-bootstrap-cross-tab-yielded'
  | 'sed-bootstrap-stale-lock-broken'
  | 'sed-bootstrap-resumed-from-checkpoint'
  | 'sed-bootstrap-checkpoint-stale'
  | 'sed-bootstrap-admin-lock-break'
  | 'cost-warn-fired'
  | 'cost-block-fired'
  | 'cost.report_budget_exceeded' // R-131-COST-A
  | 'cost.approaching_daily_limit_email_queued' // R-131-COST-A
  | 'cost.call_near_output_cap' // R-131-COST-A
  | 'gemini-call-cost-computed'
  | 'qualitative-claim-audit-completed'
  | 'absence-assertion-audit-completed'
  | 'absence-assertion-violation'
  | 'section-coherence-audit-completed'
  | 'citation-support-verification-completed'
  | 'sentiment-fetch-hit'
  | 'sentiment-fetch-miss'
  | 'sentiment-fetch-failed'
  | 'sentiment-fetcher-unkeyed'
  | 'composite-recomputed'
  | 'parcel-added'
  | 'parcel-merged'
  | 'parcel-split'
  | 'per-parcel-provider-invoked'
  | 'per-parcel-variance-flagged'
  | 'user-conflict-authored'
  | 'softened-conditional-skipped'
  | 'score-movement-chip-clicked'
  | 'dashboard-section-viewed'
  | 'dashboard-portfolio-recompute'
  | 'findings-answer-sheet-viewed'
  | 'findings-question-opened'
  | 'findings-evidence-opened'
  | 'findings-underlying-analysis-opened'
  | 'economic-contribution-brief-created'
  | 'resolver-tier3-dominant-flagged'
  | 'resolver-cross-registry-conflict'
  | 'sed-rebuild-button-clicked'
  | 'sed-rebuild-freshness-skipped'
  | 'sed-rebuild-blocked-shown'
  | 'llm.safety_block'; // R-131-LLM

export interface AuditTelemetryEvent {
  id?: string;
  type: AuditTelemetryEventType;
  timestamp: string;
  sessionId: string;
  userId?: string;
  customerId?: string;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  payload?: Record<string, unknown>;
}

export interface TelemetryExportFilters {
  from: string;
  to: string;
  siteIds?: string[];
}

export interface TelemetryBundle {
  schemaVersion: 1;
  appVersion: string;
  exportedAt: string;
  filters: TelemetryExportFilters;
  counts: {
    auditEvents: number;
    bugReports: number;
    reportGenerated: number;
    auditRuns: number;
    reconciliationBlocks: number;
    precedenceViolations: number;
    betaBannerClicks: number;
  };
  auditEvents: AuditTelemetryEvent[];
  bugReports: BugReport[];
  auditFindings: Array<{
    siteId: string;
    lastRunAt?: string;
    findingCount?: number;
    score?: { passed: number; total: number };
  }>;
  precedenceViolations: PrecedenceViolation[];
  intentSedReconciliation: IntentSedReconciliationResult[];
}

/**
 * R-106 — strip top-level undefined values + recursively strip them
 * inside `payload` (the only nested object on AuditTelemetryEvent).
 * Firestore client SDK rejects any undefined value, even on optional-
 * typed fields. Empty string / null / 0 / false are preserved.
 */
function stripUndefined<T extends object>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v === undefined) continue;
    if (k === 'payload' && v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = stripUndefined(v as Record<string, unknown>);
      continue;
    }
    out[k] = v;
  }
  return out as T;
}

const SESSION_ID_KEY = 'datacrate_sessionId';

export function getTelemetrySessionId(): string {
  if (typeof sessionStorage === 'undefined') {
    return `ssr-${Date.now()}`;
  }
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    return `fallback-${Date.now()}`;
  }
}

function resolveAppVersion(): string {
  try {
    return typeof __BUILD_HASH__ === 'string' ? __BUILD_HASH__ : 'unknown';
  } catch {
    return 'unknown';
  }
}

async function resolveCurrentCustomerId(): Promise<string | undefined> {
  try {
    const useStore = await loadAppStore();
    const fromStore = (useStore.getState() as { companyId?: string | null }).companyId;
    if (typeof fromStore === 'string' && fromStore.trim()) return fromStore;
  } catch {
    // no-op
  }
  try {
    const { auth } = await import('../../firebase');
    const token = await auth.currentUser?.getIdTokenResult();
    const claim = token?.claims?.companyId;
    if (typeof claim === 'string' && claim.trim()) return claim;
  } catch {
    // no-op
  }
  return undefined;
}

/** Fire-and-forget write to `auditEvents/{autoId}`. Never throws. */
export function logAuditEvent(
  event: Omit<AuditTelemetryEvent, 'timestamp' | 'sessionId'> & {
    timestamp?: string;
    sessionId?: string;
  },
): void {
  void (async () => {
    try {
      const { auth, db } = await import('../../firebase');
      const user = auth.currentUser;
      if (!user) return;

      // R-106: resolve required IDs FIRST. If any are missing, skip the
      // write — Firestore rules require them, and the client SDK rejects
      // undefined values locally anyway.
      const customerId = await resolveCurrentCustomerId();
      if (!customerId) {
        console.debug(
          `[auditTelemetry] skipping ${event.type} — customerId not yet available`,
        );
        return;
      }

      // R-106: build the row, then strip undefined values across the
      // whole shape. Callers legitimately pass siteId/reportId/etc. as
      // undefined when no site is open; Firestore can't accept undefined
      // even when the field is optional in the rule.
      const rawRow: AuditTelemetryEvent = {
        ...event,
        timestamp: event.timestamp ?? new Date().toISOString(),
        sessionId: event.sessionId ?? getTelemetrySessionId(),
        userId: user.uid,
        customerId,
        payload: {
          ...(event.payload ?? {}),
        appVersion: resolveAppVersion(),
        },
      };
      const row = stripUndefined(rawRow);

      const { collection, addDoc } = await import('firebase/firestore');
      await addDoc(collection(db, 'auditEvents'), row);
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      const code = (err as { code?: string })?.code ?? '';
      // ALREADY_EXISTS is benign for this fire-and-forget telemetry write: it
      // means the event is effectively recorded (e.g. an offline-replay /
      // double-fire of the same logical event), and no duplicate was written.
      // Downgrade it to debug so it isn't console noise; real failures still warn.
      if (code === 'already-exists' || /already exists/i.test(msg)) {
        console.debug('[auditTelemetry] logAuditEvent skipped (already recorded):', msg);
      } else {
        console.warn('[auditTelemetry] logAuditEvent failed:', msg);
      }
    }
  })();
}

export async function exportTelemetryBundle(
  filters: TelemetryExportFilters,
): Promise<TelemetryBundle> {
  const params = new URLSearchParams({
    from: filters.from,
    to: filters.to,
  });
  if (filters.siteIds?.length) {
    params.set('siteIds', filters.siteIds.join(','));
  }
  const res = await authedFetch(`/api/telemetry/export?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Telemetry export failed: ${res.status} ${await res.text()}`);
  }
  return await res.json() as TelemetryBundle;
}

export async function downloadTelemetryBundle(filters: TelemetryExportFilters): Promise<void> {
  const bundle = await exportTelemetryBundle(filters);
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `deepatomic-audit-telemetry-${filters.from}-to-${filters.to}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
